import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import SuperDocEditor from './SuperDocEditor';

const PropertyLeasesSection = ({ propertyId }) => {
  const navigate = useNavigate();
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('active'); // active, expiring, all
  
  // Property-level lease document state
  const [propertyLeaseDoc, setPropertyLeaseDoc] = useState(null);
  const [boilerplateInfo, setBoilerplateInfo] = useState(null);
  const [loadingPropertyLease, setLoadingPropertyLease] = useState(true);
  const [editingPropertyLease, setEditingPropertyLease] = useState(false);
  const [propertyLeaseFile, setPropertyLeaseFile] = useState(null);
  const [availableBoilerplates, setAvailableBoilerplates] = useState([]);
  const [showBoilerplateModal, setShowBoilerplateModal] = useState(false);

  useEffect(() => {
    fetchLeases();
    fetchPropertyLeaseDocument();
    fetchAvailableBoilerplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, filter]);

  const fetchLeases = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/properties/${propertyId}/leases`;
      const params = new URLSearchParams();
      
      if (filter === 'active') {
        params.append('status', 'active');
      } else if (filter === 'expiring') {
        params.append('status', 'active');
        params.append('include_expiring', 'true');
        params.append('days_ahead', '90');
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setLeases(data);
      }
    } catch (err) {
      console.error('Error fetching leases:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const fetchPropertyLeaseDocument = async () => {
    if (!propertyId) return;
    setLoadingPropertyLease(true);
    try {
      // Check if property document exists
      const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document`);
      if (response.ok) {
        const data = await response.json();
        if (data.property_doc_id) {
          setPropertyLeaseDoc({
            id: data.property_doc_id,
            boilerplate_id: data.boilerplate_template_id,
            boilerplate_name: data.boilerplate_name
          });
          
          // Get boilerplate info
          if (data.boilerplate_template_id) {
            setBoilerplateInfo({
              id: data.boilerplate_template_id,
              name: data.boilerplate_name || 'Lease Template'
            });
          }
          
          // Load the resolved document for editing
          try {
            const resolvedResponse = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document/resolved`);
            const contentType = resolvedResponse.headers.get('content-type') || '';
            
            // Check if it's a successful file response
            if (resolvedResponse.ok && (
              contentType.includes('application/vnd.openxmlformats') || 
              contentType.includes('application/octet-stream') ||
              contentType.includes('application/zip') ||
              resolvedResponse.status === 200
            )) {
              // It's a DOCX file (or should be)
              try {
                const blob = await resolvedResponse.blob();
                // Verify it's actually a DOCX file by checking blob type or size
                if (blob.size > 0) {
                  const file = new File([blob], 'property_lease.docx', { 
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                  });
                  setPropertyLeaseFile(file);
                  console.log('Successfully loaded resolved property document');
                } else {
                  throw new Error('File is empty');
                }
              } catch (blobErr) {
                console.error('Error reading file blob:', blobErr);
                // Fall through to error handling
                throw blobErr;
              }
            } else {
              // It's an error response (JSON)
              let errorData;
              try {
                errorData = await resolvedResponse.json();
              } catch (jsonErr) {
                // Response might not be JSON, try to read as text
                const text = await resolvedResponse.text();
                errorData = { error: text || 'Unknown error', status: resolvedResponse.status };
              }
              
              console.error('Resolved document error:', errorData);
              
              // Try multiple fallback strategies
              let fileLoaded = false;
              
              // Strategy 1: Try to get any lease document without property_id (global boilerplate)
              try {
                const docsResponse = await fetch(`${API_BASE_URL}/api/documents`);
                if (docsResponse.ok) {
                  const allDocs = await docsResponse.json();
                  const leaseDoc = allDocs.find(d => 
                    (d.category === 'lease' || d.name?.toLowerCase().includes('lease')) &&
                    (!d.property_id || d.property_id === 0)
                  );
                  
                  if (leaseDoc && leaseDoc.download_url) {
                    console.log('Trying to load global lease document:', leaseDoc.name);
                    const fileResponse = await fetch(`${API_BASE_URL}${leaseDoc.download_url}`);
                    if (fileResponse.ok) {
                      const blob = await fileResponse.blob();
                      if (blob.size > 0) {
                        const file = new File([blob], 'property_lease.docx', { 
                          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                        });
                        setPropertyLeaseFile(file);
                        fileLoaded = true;
                        console.log('Successfully loaded global lease document as fallback');
                      }
                    }
                  }
                }
              } catch (fallbackErr) {
                console.error('Error loading boilerplate as fallback:', fallbackErr);
              }
              
              // Strategy 2: Try property-specific documents
              if (!fileLoaded) {
                try {
                  const propDocsResponse = await fetch(`${API_BASE_URL}/api/documents?property_id=${propertyId}`);
                  if (propDocsResponse.ok) {
                    const propDocs = await propDocsResponse.json();
                    const leaseDoc = propDocs.find(d => 
                      d.category === 'lease' || d.name?.toLowerCase().includes('lease')
                    );
                    
                    if (leaseDoc && leaseDoc.download_url) {
                      console.log('Trying to load property-specific lease document:', leaseDoc.name);
                      const fileResponse = await fetch(`${API_BASE_URL}${leaseDoc.download_url}`);
                      if (fileResponse.ok) {
                        const blob = await fileResponse.blob();
                        if (blob.size > 0) {
                          const file = new File([blob], 'property_lease.docx', { 
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                          });
                          setPropertyLeaseFile(file);
                          fileLoaded = true;
                          console.log('Successfully loaded property-specific lease document as fallback');
                        }
                      }
                    }
                  }
                } catch (fallbackErr2) {
                  console.error('Error loading property document as fallback:', fallbackErr2);
                }
              }
              
              if (!fileLoaded) {
                console.warn('Could not load any lease document file. User will need to upload one.');
              }
            }
          } catch (err) {
            console.error('Error loading resolved property document:', err);
          }
        } else if (data.boilerplate_template_id) {
          // Boilerplate exists but property doc doesn't - auto-create it
          try {
            const createResponse = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document-from-boilerplate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ boilerplate_template_id: data.boilerplate_template_id })
            });
            if (createResponse.ok) {
              const createData = await createResponse.json();
              setPropertyLeaseDoc({
                id: createData.property_doc_id,
                boilerplate_id: createData.boilerplate_template_id
              });
              setBoilerplateInfo({
                id: createData.boilerplate_template_id,
                name: data.boilerplate_name || 'Lease Template'
              });
              // Reload resolved document
              await fetchPropertyLeaseDocument();
            }
          } catch (err) {
            console.error('Error auto-creating property document:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching property lease document:', err);
    } finally {
      setLoadingPropertyLease(false);
    }
  };

  const fetchAvailableBoilerplates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/document-layers/boilerplate`);
      if (response.ok) {
        const data = await response.json();
        setAvailableBoilerplates(data);
      }
    } catch (err) {
      console.error('Error fetching boilerplates:', err);
    }
  };

  const handleChangeBoilerplate = async (newBoilerplateId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document/change-boilerplate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boilerplate_template_id: newBoilerplateId })
      });
      
      if (response.ok) {
        alert('Boilerplate template changed successfully!');
        setShowBoilerplateModal(false);
        await fetchPropertyLeaseDocument();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change boilerplate');
      }
    } catch (err) {
      console.error('Error changing boilerplate:', err);
      alert(`Failed to change boilerplate: ${err.message}`);
    }
  };

  const handleDisconnectBoilerplate = async () => {
    if (!window.confirm('Are you sure you want to disconnect this property lease from the boilerplate? It will become standalone and no longer receive updates from the boilerplate.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document/disconnect-boilerplate`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        alert('Property lease disconnected from boilerplate. It is now standalone.');
        await fetchPropertyLeaseDocument();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disconnect boilerplate');
      }
    } catch (err) {
      console.error('Error disconnecting boilerplate:', err);
      alert(`Failed to disconnect boilerplate: ${err.message}`);
    }
  };

  const handleSavePropertyLease = async (documentBlob, documentId) => {
    // Save property document edits - this saves as property-specific and creates overrides
    // This does NOT overwrite the boilerplate template
    try {
      const file = new File([documentBlob], 'property_lease.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Use the new endpoint that saves property-specific edits
      const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document/save-edits`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('Property lease document saved! Changes are property-specific and do not affect the boilerplate template.');
        setEditingPropertyLease(false);
        // Reload to get the updated file
        await fetchPropertyLeaseDocument();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to save document' }));
        throw new Error(errorData.error || 'Failed to save document');
      }
    } catch (err) {
      console.error('Error saving property lease:', err);
      alert(`Failed to save property lease: ${err.message}`);
    }
  };

  const getExpiringSoonLeases = () => {
    return leases.filter(lease => lease.expiring_soon && !lease.is_document);
  };

  const expiringLeases = getExpiringSoonLeases();
  
  // Filter out document templates from tenant leases list
  const tenantLeases = leases.filter(lease => !lease.is_document);

  // If editing property lease, show editor
  if (editingPropertyLease) {
    if (!propertyLeaseFile) {
      return (
        <div className="property-leases-section">
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setEditingPropertyLease(false)}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '16px'
              }}
            >
              ← Back to Leases
            </button>
            <div style={{
              padding: '12px',
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <strong>⚠️ No Document File Found</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                The property lease document doesn't have a file yet. Please upload a lease document first.
              </p>
              <div style={{ marginTop: '12px' }}>
                <label style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: '#1457f5',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  📤 Upload Lease Document
                  <input
                    type="file"
                    accept=".docx"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file && file.name.endsWith('.docx')) {
                        // Upload the file
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('property_id', propertyId);
                        formData.append('category', 'lease');
                        formData.append('name', 'Property Lease Agreement');
                        
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
                            method: 'POST',
                            body: formData
                          });
                          
                          if (response.ok) {
                            // Use the uploaded file for editing
                            setPropertyLeaseFile(file);
                            alert('Document uploaded! You can now edit it.');
                          } else {
                            const errorData = await response.json();
                            alert(`Failed to upload: ${errorData.error || 'Unknown error'}`);
                          }
                        } catch (err) {
                          alert(`Failed to upload document: ${err.message}`);
                        }
                      } else {
                        alert('Please select a .docx file');
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="property-leases-section">
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => setEditingPropertyLease(false)}
            style={{
              padding: '8px 16px',
              background: '#64748b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '16px'
            }}
          >
            ← Back to Leases
          </button>
          <div style={{
            padding: '12px',
            background: '#e7f0ff',
            border: '1px solid #1457f5',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#1457f5'
          }}>
            <strong>ℹ️ Editing Property Lease Document</strong>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
              {boilerplateInfo ? (
                <>This document inherits from the boilerplate template. Your edits will create property-specific overrides and will not modify the boilerplate. Inherits from: <strong>{boilerplateInfo.name}</strong></>
              ) : (
                <>This is a standalone property lease document. Edits will only affect this property.</>
              )}
            </p>
          </div>
        </div>
        <SuperDocEditor
          documentId={`prop_lease_${propertyId}`}
          documentFile={propertyLeaseFile}
          onSave={handleSavePropertyLease}
          onCancel={() => setEditingPropertyLease(false)}
        />
      </div>
    );
  }

  return (
    <div className="property-leases-section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Leases</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => navigate(`/legal?tab=Documents&propertyId=${propertyId}`)}
            style={{
              padding: '8px 16px',
              background: '#374151',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ⚖️ New Legal Matter
          </button>
          <button
            onClick={() => setFilter('active')}
            style={{
              padding: '8px 16px',
              border: filter === 'active' ? '2px solid #1457f5' : '1px solid #e2e8f0',
              background: filter === 'active' ? '#e7f0ff' : 'white',
              color: filter === 'active' ? '#1457f5' : '#64748b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Active
          </button>
          <button
            onClick={() => setFilter('expiring')}
            style={{
              padding: '8px 16px',
              border: filter === 'expiring' ? '2px solid #1457f5' : '1px solid #e2e8f0',
              background: filter === 'expiring' ? '#e7f0ff' : 'white',
              color: filter === 'expiring' ? '#1457f5' : '#64748b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Expiring Soon
          </button>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '8px 16px',
              border: filter === 'all' ? '2px solid #1457f5' : '1px solid #e2e8f0',
              background: filter === 'all' ? '#e7f0ff' : 'white',
              color: filter === 'all' ? '#1457f5' : '#64748b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            All
          </button>
        </div>
      </div>

      {/* Property-Level Lease Document Section */}
      <div style={{
        marginBottom: '32px',
        padding: '20px',
        background: 'white',
        border: '2px solid #1457f5',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, marginBottom: '8px', fontSize: '20px', color: '#1e293b' }}>
              Property Lease Agreement
            </h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              {boilerplateInfo ? (
                <>Inherits from: <strong style={{ color: '#1457f5' }}>{boilerplateInfo.name}</strong></>
              ) : (
                'Standalone lease template for this property'
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {propertyLeaseDoc ? (
              <>
                <button
                  onClick={async () => {
                    if (propertyLeaseFile) {
                      setEditingPropertyLease(true);
                    } else {
                      // Try to load file first
                      await fetchPropertyLeaseDocument();
                      // Wait a bit for state to update, then check again
                      setTimeout(() => {
                        if (propertyLeaseFile) {
                          setEditingPropertyLease(true);
                        } else {
                          // Show upload UI instead of alert
                          setEditingPropertyLease(true); // Show the editor with upload option
                        }
                      }, 500);
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#1457f5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  ✏️ Edit Property Lease
                </button>
                {boilerplateInfo && (
                  <>
                    <button
                      onClick={() => setShowBoilerplateModal(true)}
                      style={{
                        padding: '10px 16px',
                        background: 'white',
                        color: '#1457f5',
                        border: '1px solid #1457f5',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                      title="Change boilerplate template"
                    >
                      🔄 Change Template
                    </button>
                    <button
                      onClick={handleDisconnectBoilerplate}
                      style={{
                        padding: '10px 16px',
                        background: 'white',
                        color: '#ef4444',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                      title="Disconnect from boilerplate (make standalone)"
                    >
                      🔌 Disconnect
                    </button>
                  </>
                )}
              </>
            ) : loadingPropertyLease ? (
              <div style={{ color: '#64748b', fontSize: '14px' }}>Loading...</div>
            ) : (
              <button
                onClick={async () => {
                  // Create property document from boilerplate
                  try {
                    const response = await fetch(`${API_BASE_URL}/api/document-layers/property/${propertyId}/document-from-boilerplate`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({})
                    });
                    if (response.ok) {
                      await fetchPropertyLeaseDocument();
                    }
                  } catch (err) {
                    alert('Failed to create property lease document');
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                📋 Create from Boilerplate
              </button>
            )}
          </div>
        </div>
        <div style={{
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#64748b'
        }}>
          {propertyLeaseDoc ? (
            <>This is the property-level lease document. All tenant leases for this property will inherit from this document. Edits here create property-specific overrides that apply to all tenants.</>
          ) : (
            <>No property lease document exists yet. Create one from the boilerplate template to customize the lease for this property.</>
          )}
        </div>
      </div>

      {/* Tenant Leases Section */}
      <div style={{ marginTop: '32px' }}>
        <h3 style={{ marginBottom: '20px', fontSize: '18px', color: '#1e293b' }}>Tenant Leases</h3>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading leases...</div>
      ) : (
        <>
          {/* Expiring Soon Alert */}
          {filter === 'active' && expiringLeases.length > 0 && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              background: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '8px',
              color: '#856404'
            }}>
              <strong>⚠️ {expiringLeases.length} lease{expiringLeases.length !== 1 ? 's' : ''} expiring in the next 90 days</strong>
            </div>
          )}

          {tenantLeases.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              No {filter === 'expiring' ? 'expiring' : filter === 'active' ? 'active' : ''} tenant leases found.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {tenantLeases.map(lease => (
                <div key={lease.id} style={{
                  padding: '20px',
                  background: 'white',
                  borderRadius: '8px',
                  border: lease.expiring_soon ? '2px solid #ffc107' : lease.is_document ? '2px solid #e2e8f0' : '1px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: lease.is_document ? 0.9 : 1
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                    <div>
                      <h3 style={{ margin: 0, marginBottom: '8px', fontSize: '18px' }}>
                        {lease.tenant_name}
                      </h3>
                      <div style={{ fontSize: '14px', color: '#64748b' }}>
                        Unit {lease.unit_number}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {lease.expiring_soon && (
                        <span style={{
                          padding: '4px 12px',
                          background: '#fff3cd',
                          color: '#856404',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          Expiring
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>Rent Amount:</span>
                        <span style={{ fontWeight: 600 }}>{formatCurrency(lease.rent_amount)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>Start Date:</span>
                        <span>{formatDate(lease.start_date)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>End Date:</span>
                        <span>{formatDate(lease.end_date) || 'Ongoing'}</span>
                      </div>
                      {lease.days_until_expiration !== undefined && lease.days_until_expiration !== null && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b', fontSize: '14px' }}>Days Until Expiration:</span>
                          <span style={{ 
                            color: lease.days_until_expiration <= 30 ? '#ef4444' : lease.days_until_expiration <= 60 ? '#ff9800' : '#64748b',
                            fontWeight: 600
                          }}>
                            {lease.days_until_expiration} days
                          </span>
                        </div>
                      )}
                      {lease.due_day && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b', fontSize: '14px' }}>Due Day:</span>
                          <span>Day {lease.due_day} of month</span>
                        </div>
                      )}
                    </div>

                  <div style={{ marginTop: 'auto', display: 'flex', gap: '10px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                      onClick={() => navigate(`/tenant/${lease.tenant_id}`)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: '#1457f5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      View Tenant
                    </button>
                    <button
                      onClick={() => navigate(`/tenant/${lease.tenant_id}#documents`)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'white',
                        color: '#1457f5',
                        border: '1px solid #1457f5',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      View Documents
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </div>

      {/* Change Boilerplate Modal */}
      {showBoilerplateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowBoilerplateModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>Change Boilerplate Template</h3>
            <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>
              Select a different boilerplate template for this property. The property document will inherit from the new template.
            </p>
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px' }}>
              {availableBoilerplates.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                  No boilerplate templates available
                </div>
              ) : (
                availableBoilerplates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => handleChangeBoilerplate(template.id)}
                    style={{
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      background: template.id === boilerplateInfo?.id ? '#e7f0ff' : 'white',
                      borderColor: template.id === boilerplateInfo?.id ? '#1457f5' : '#e2e8f0'
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>{template.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {template.category} • {template.source === 'new_system' ? 'New System' : 'Legacy'}
                      {template.id === boilerplateInfo?.id && ' (Current)'}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowBoilerplateModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyLeasesSection;

